
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DateRangeSelector, { DateRange } from "@/components/DateRangeSelector";
import PaymentStatsGrid from "./PaymentStatsGrid";
import { PaymentStats } from "@/hooks/usePaymentStats";
import { format } from "date-fns";

interface PaymentDashboardContentProps {
  stats: PaymentStats | null;
  loading: boolean;
  onDateRangeChange: (range: DateRange) => void;
  currentDateRange: DateRange;
}

const PaymentDashboardContent = ({ 
  stats, 
  loading, 
  onDateRangeChange, 
  currentDateRange 
}: PaymentDashboardContentProps) => {
  const navigate = useNavigate();

  const navigateToCustomers = () => {
    navigate('/customers');
  };

  const showAllData = () => {
    // Set a very wide date range to show all data
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, 0, 1);
    const oneYearFromNow = new Date(today.getFullYear() + 1, 11, 31);
    
    onDateRangeChange({
      from: oneYearAgo,
      to: oneYearFromNow
    });
  };




  const hasNoData = !stats || stats.total === 0;

  const emptyStats: PaymentStats = {
    total: 0,
    paid: 0,
    pending: 0,
    estimateCount: 0,
    totalAmount: 0,
    paidAmount: 0,
    estimateAmount: 0,
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <CardTitle>Payment Overview</CardTitle>
        <DateRangeSelector onRangeChange={onDateRangeChange} />
      </CardHeader>
      <CardContent>
        <div className="mb-4 text-sm text-muted-foreground flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>
            Showing data from {format(currentDateRange.from, "MMM dd, yyyy")} to {format(currentDateRange.to, "MMM dd, yyyy")}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <PaymentStatsGrid stats={stats ?? emptyStats} />
        )}

        {!loading && hasNoData && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-dashed p-3">
            <p className="text-sm text-muted-foreground">
              No payment data in this range.
            </p>
            <Button onClick={showAllData} variant="outline" size="sm" className="gap-2">
              <Calendar size={16} />
              Show All Data
            </Button>
          </div>
        )}

        <div className="mt-6 flex justify-start">
          <Button onClick={navigateToCustomers} variant="outline" className="gap-2">
            <Users size={16} />
            View Customers
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


export default PaymentDashboardContent;
